import "./_style.less";
import { useTranslation } from "react-i18next";
import { useDisclosure } from "../../../../hooks/use-disclosure";
import { Col, Modal, Row } from "antd";
import {
  VuiButton,
  VuiCalendarReschedule,
  VuiSecondaryTitleWrapper,
} from "../../../../@vendor/components";
import { CloseIcon } from "../../../../components";
import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Schedule } from "../../../../models/Schedule";
import moment, { Moment } from "moment";
import clsx from "clsx";

interface IAssessmentScheduleCalendar {
  data: Schedule[];
  selectedData?: string[];
  onSelectDate?: (date: Moment) => void;
  disabledDate?: (date: Moment) => boolean;
  multiSelect?: boolean;
  selectionLimit?: number;
  minDaysBetween?: number[];
  onApplyDates?: (dates: string[]) => void;
}

const AssessmentScheduleCalendar = ({
  data,
  selectedData,
  onSelectDate,
  disabledDate,
  multiSelect = false,
  selectionLimit,
  minDaysBetween,
  onApplyDates,
}: IAssessmentScheduleCalendar) => {
  const { t } = useTranslation();
  const { onClose, onOpen, isOpen } = useDisclosure();
  const [tempDates, setTempDates] = useState<string[]>([]);
  const [lastClicked, setLastClicked] = useState<string | null>(null);

  const handleOpen = useCallback(() => {
    setTempDates(selectedData || []);
    setLastClicked(null);
    onOpen();
  }, [onOpen, selectedData]);

  const handleCancel = useCallback(() => {
    setTempDates([]);
    setLastClicked(null);
    onClose();
  }, [onClose]);

  const handleApply = useCallback(() => {
    if (multiSelect && onApplyDates) {
      onApplyDates(tempDates);
    } else if (onSelectDate) {
      const dk = tempDates[0];
      if (dk) onSelectDate(moment(dk, "YYYY-MM-DD"));
    }
    setTempDates([]);
    setLastClicked(null);
    onClose();
  }, [
    lastClicked,
    multiSelect,
    onApplyDates,
    onClose,
    onSelectDate,
    tempDates,
  ]);

  const calendarDisabledDate = useCallback(
    (d: Moment) => {
      if (disabledDate) return disabledDate(d);
      if (!d || typeof d.format !== "function") return false;

      const dk = d.format("YYYY-MM-DD");
      const current = tempDates;
      try {
        const candidateTime = new Date(dk).getTime();
        const existingDatesTimes = current
          .filter((x) => !!x)
          .map((x) => new Date(x).getTime())
          .filter((t) => !Number.isNaN(t));
        if (existingDatesTimes.length > 0) {
          const maxExisting = Math.max(...existingDatesTimes);
          if (!Number.isNaN(candidateTime) && candidateTime < maxExisting)
            return true;
        }
      } catch (err) {
        // ignore and continue
      }
      const isSelected = current.includes(dk);

      const limit =
        typeof selectionLimit === "number" && selectionLimit > 0
          ? selectionLimit
          : Infinity;
      if (!isSelected && current.length >= limit) return true;

      if (Array.isArray(minDaysBetween) && minDaysBetween.length > 0) {
        const set = new Set(current);
        set.add(dk);
        const list = Array.from(set).sort(); // YYYY-MM-DD lex sort works
        const idx = list.indexOf(dk);
        if (idx > 0) {
          const prev = list[idx - 1];
          const minPrev = minDaysBetween[idx - 1] || 0;
          if (minPrev > 0) {
            const diff =
              (new Date(dk).getTime() - new Date(prev).getTime()) /
              (1000 * 60 * 60 * 24);
            if (diff < minPrev) return true;
          }
        }
        if (idx < list.length - 1) {
          const next = list[idx + 1];
          const minThis = minDaysBetween[idx] || 0;
          if (minThis > 0) {
            const diff =
              (new Date(next).getTime() - new Date(dk).getTime()) /
              (1000 * 60 * 60 * 24);
            if (diff < minThis) return true;
          }
        }
      }
      return false;
    },
    [
      disabledDate,
      minDaysBetween,
      multiSelect,
      selectedData,
      selectionLimit,
      tempDates,
    ]
  );

  const formatSelectedDate = useCallback((dateString?: string) => {
    if (!dateString) return "";
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return "";
    const weekday = d.toLocaleDateString(undefined, {
      weekday: "short",
    } as any);
    const day = d.getDate();
    const month = d.toLocaleDateString(undefined, { month: "short" } as any);
    return `${weekday}, ${day} ${month}`;
  }, []);

  const visibleDateSet = React.useMemo(() => {
    const s = new Set<string>();
    (data || []).slice(0, 6).forEach((sc) => sc?.date && s.add(sc.date));
    return s;
  }, [data]);

  const renderTriggerSuffix = React.useCallback((): React.ReactNode => {
    if (!Array.isArray(selectedData) || selectedData.length === 0) return null;
    const notVisible = selectedData.filter(
      (d) => !!d && !visibleDateSet.has(d)
    );
    if (notVisible.length === 0) return null;
    if (notVisible.length === 1) {
      const d = notVisible[0];
      const label = formatSelectedDate(d);
      return label ? ` (${label})` : null;
    }
    const count = notVisible.length;
    return <span className="other-dates-badge">{count}</span>;
  }, [selectedData, formatSelectedDate, visibleDateSet]);

  const scheduleDates = useMemo(() => {
    return data.slice(0, 6).map((d) => d?.date);
  }, [data]);

  const otherScheduleDates = useMemo(() => {
    return selectedData?.filter((s) => !scheduleDates.includes(s)) ?? [];
  }, [scheduleDates, selectedData]);

  return (
    <div className="assessment-schedule-calendar">
      <Modal
        footer={null}
        closable={false}
        visible={isOpen}
        className="default-modal"
        title={null}
        centered
      >
        <div className="default-modal-header">
          <VuiSecondaryTitleWrapper
            title={t("common.text.chooseADate")}
            level={4}
          >
            <CloseIcon onClick={onClose} />
          </VuiSecondaryTitleWrapper>
        </div>

        <div className="calendar-section">
          <div className="calendar-box">
            <VuiCalendarReschedule
              onSelectDate={(d) => {
                const dk = d.format("YYYY-MM-DD");
                const hasScheduleForDateLocal =
                  Array.isArray(data) &&
                  data.some((sc) => sc && sc.date === dk);
                if (!hasScheduleForDateLocal) return;
                setLastClicked(dk);
                setTempDates((prev) => {
                  let cloneState = [...prev];
                  if (multiSelect) {
                    if (cloneState.includes(dk)) {
                      cloneState = cloneState.filter((cs) => cs !== dk);
                    } else {
                      const limit =
                        typeof selectionLimit === "number" && selectionLimit > 0
                          ? selectionLimit
                          : Infinity;
                      if (cloneState.length >= limit) {
                        return cloneState;
                      }
                      cloneState.push(dk);
                    }
                  } else {
                    cloneState = [dk];
                  }
                  return cloneState;
                });
              }}
              data={data}
              selectedDates={tempDates}
              disabledDate={calendarDisabledDate}
            />
          </div>

          <div className="schedule-calendar-helpers">
            <div className="schedule-calendar-helper">
              <span className="bullet available" />
              <span>{t("common.text.available")}</span>
            </div>
            <div className="schedule-calendar-helper">
              <span className="bullet chosen" />
              <span>{t("common.text.chosen")}</span>
            </div>
            <div className="schedule-calendar-helper">
              <span className="line" />
              <span>{t("common.text.today")}</span>
            </div>
          </div>
        </div>

        <Row gutter={[12, 12]}>
          <Col md={12} xs={24}>
            <VuiButton
              buttonProps={{ onClick: handleCancel }}
              label={t("common.button.cancel")}
            />
          </Col>
          <Col md={12} xs={24}>
            <VuiButton
              buttonProps={{ type: "primary", onClick: handleApply }}
              label={t("common.button.apply")}
            />
          </Col>
        </Row>
      </Modal>

      <div
        className={clsx("schedule-calendar-trigger", {
          active: otherScheduleDates.length > 0,
        })}
        onClick={handleOpen}
      >
        <span className="schedule-calendar-trigger-text">
          {t("common.text.otherDates")}
        </span>
        {renderTriggerSuffix()}
      </div>
    </div>
  );
};

export default AssessmentScheduleCalendar;
